import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import mapSessionService from '../services/mapSessionService'
import LeafletComparisonMap from '../components/map/LeafletComparisonMap'
import {
  HiOutlineMap,
  HiOutlineLocationMarker,
  HiOutlinePhone,
  HiOutlineShare,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast from 'react-hot-toast'

function SharedMapView() {
  const { sessionCode } = useParams()
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadSession()
  }, [sessionCode])

  const loadSession = async () => {
    try {
      const response = await mapSessionService.getSessionByCode(sessionCode)
      if (response.status === 'success') {
        setSession(response.data)
      } else {
        setError('Không tìm thấy phiên so sánh')
      }
    } catch (err) {
      console.error('Failed to load session:', err)
      setError('Không thể tải phiên so sánh')
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (price) => {
    if (!price) return '-'
    if (price >= 1000000000) {
      return `${(price / 1000000000).toFixed(1)} tỷ`
    }
    return `${(price / 1000000).toFixed(0)} triệu`
  }

  const getSegmentLabel = (segment) => {
    const labels = {
      cao_cap: 'Cao cấp',
      trung_cap: 'Trung cấp',
      binh_dan: 'Bình dân',
      nghi_duong: 'Nghỉ dưỡng',
    }
    return labels[segment] || segment
  }

  const handleShare = async () => {
    const url = window.location.href
    try {
      if (navigator.share) {
        await navigator.share({
          title: session.name,
          text: `So sánh giá dự án: ${session.center_project?.name}`,
          url,
        })
      } else {
        await navigator.clipboard.writeText(url)
        toast.success('Đã copy link!')
      }
    } catch (err) {
      console.error('Share failed:', err)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-10 h-10 mx-auto mb-4" />
          <p className="text-gray-500">Đang tải bản đồ so sánh...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-6">
          <HiOutlineMap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Không tìm thấy</h1>
          <p className="text-gray-500 mb-4">{error}</p>
          <Link to="/" className="btn-primary">
            Về trang chủ
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <HiOutlineLocationMarker className="w-6 h-6 text-primary-500" />
              <div>
                <h1 className="font-semibold text-gray-900">{session.name}</h1>
                <p className="text-xs text-gray-500">
                  So sánh trong bán kính {session.radius_km}km
                </p>
              </div>
            </div>
            <button
              onClick={handleShare}
              className="btn-outline btn-sm"
            >
              <HiOutlineShare className="w-4 h-4 mr-1" />
              Chia sẻ
            </button>
          </div>
        </div>
      </header>

      {/* Map */}
      <div className="h-[50vh] lg:h-[60vh]">
        <LeafletComparisonMap
          centerProject={session.center_project}
          nearbyProjects={session.nearby_projects || []}
          radiusKm={session.radius_km}
        />
      </div>

      {/* Project List */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Center Project */}
        <div className="card p-4 mb-4 border-2 border-primary-500">
          <div className="flex items-start justify-between">
            <div>
              <span className="badge-primary text-xs mb-2">DỰ ÁN TRUNG TÂM</span>
              <h2 className="font-semibold text-lg text-gray-900">
                {session.center_project?.name}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {session.center_project?.address || session.center_project?.location}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-primary-600">
                {formatPrice(session.center_project?.price_from)} - {formatPrice(session.center_project?.price_to)}
              </p>
              {session.center_project?.price_per_m2 && (
                <p className="text-sm text-gray-500">
                  {(session.center_project.price_per_m2 / 1000000).toFixed(0)} tr/m²
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Nearby Projects */}
        {session.nearby_projects?.length > 0 && (
          <>
            <h3 className="font-semibold text-gray-900 mb-3">
              {session.nearby_projects.length} Dự án lân cận
            </h3>
            <div className="space-y-3">
              {session.nearby_projects.map((project) => {
                const centerPrice = session.center_project?.price_per_m2 || 0
                const projectPrice = project.price_per_m2 || 0
                const priceDiff = centerPrice && projectPrice
                  ? ((projectPrice - centerPrice) / centerPrice * 100).toFixed(0)
                  : null

                return (
                  <div key={project.id} className="card p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={clsx(
                              'px-2 py-0.5 rounded text-xs font-medium',
                              project.segment === 'cao_cap' && 'bg-purple-100 text-purple-700',
                              project.segment === 'trung_cap' && 'bg-blue-100 text-blue-700',
                              project.segment === 'binh_dan' && 'bg-green-100 text-green-700',
                              project.segment === 'nghi_duong' && 'bg-amber-100 text-amber-700'
                            )}
                          >
                            {getSegmentLabel(project.segment)}
                          </span>
                          {project.distance_km && (
                            <span className="text-xs text-gray-500">
                              {project.distance_km} km
                            </span>
                          )}
                        </div>
                        <h4 className="font-medium text-gray-900">{project.name}</h4>
                        <p className="text-sm text-gray-500">
                          {project.address || project.location}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-gray-900">
                          {formatPrice(project.price_from)} - {formatPrice(project.price_to)}
                        </p>
                        {project.price_per_m2 && (
                          <p className="text-sm text-gray-500">
                            {(project.price_per_m2 / 1000000).toFixed(0)} tr/m²
                          </p>
                        )}
                        {priceDiff !== null && (
                          <p
                            className={clsx(
                              'text-sm font-medium mt-1',
                              parseInt(priceDiff) > 0 ? 'text-red-600' : 'text-green-600'
                            )}
                          >
                            {parseInt(priceDiff) > 0 ? '+' : ''}{priceDiff}% so với trung tâm
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}

        {/* Contact CTA */}
        <div className="mt-6 card p-6 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-center">
          <h3 className="font-semibold text-lg mb-2">Quan tâm đến dự án nào?</h3>
          <p className="text-primary-100 text-sm mb-4">
            Liên hệ ngay để được tư vấn chi tiết
          </p>
          <a
            href="tel:0905123456"
            className="inline-flex items-center px-6 py-3 bg-white text-primary-600 rounded-xl font-medium hover:bg-primary-50 transition-colors"
          >
            <HiOutlinePhone className="w-5 h-5 mr-2" />
            Gọi ngay: 0905 123 456
          </a>
        </div>
      </div>
    </div>
  )
}

export default SharedMapView
